import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../rootReducer';
import { AgGridReact } from 'ag-grid-react';
import { Link } from 'react-router-dom';
import { ColDef } from 'ag-grid-community';
import { exportToCsv, handleExportClick } from '../service/csvExporter';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios, { AxiosResponse } from 'axios';
import Navbar from '../navbar/Navbar';
import logoInProgressColored from '../assets/icon-inprogress-colored.svg';
import logoFinishedColored from '../assets/icon-finished-colored.svg';
import logoDraftColored from '../assets/draft.svg';
import logoFbColored from '../assets/icon-facebook-colored.svg';
import logoInstaColored from '../assets/icon-instagram-colored.svg';
import logoGmbColored from '../assets/icon-google-colored.svg';
import logoInColored from '../assets/icon-linkedin-colored.svg';
import archive from '../assets/archive.svg';
import editOn from '../assets/editOn.svg';
import stat from '../assets/stat.svg';
import progPubli from '../assets/progPubli.svg';
import unselected from '../assets/unselected.svg';
import Pagination from '../pagination/Pagination';
import Spinner from '../spinner/Spinner';
import mediaUrl from '../service/mediaUrl';
import Filter from '../filter/filter';
import DateRangeComponent from '../daterangepicker/DateRangeComponent';
import format from 'date-fns/format';
import logoNF from '../assets/logo_news_factory.svg';
import visibility from '../assets/visibility.png';
import Modal from '../modal/Modal';
import AutoPubSwitch from './AutoPubSwitch';
import LogoSwitch from './LogoSwitch';
import searchIcon from '../assets/search.svg';
import downloadIcon from '../assets/download.svg';
import sortIcon from '../assets/sort.svg';
import dateIcon from '../assets/date.svg';
import { statusCellRenderer } from '../statusCellRenderer/statusCelleRenderer';
import { AddPublicationButton } from '../addPublicationButton/AddPublicationButton';
import { socialMediaCellRenderer } from '../socialMediaCellRenderer/socialMediaCellRenderer';

interface Publication {
    media: string;
    extension: string;
    title: string;
    content: string;
    status: string;
    createdAt: string;
    publicationDate: string;
    createdByAdmin: string;
    createdByUser: string;
    id: number;
}

const search = { path: searchIcon };
const download = { path: downloadIcon };
const sort = { path: sortIcon };
const date = { path: dateIcon };

const publicationCard = (params: any) => {
    return (
        <>
            <div className={'flex w-full items-center relative break-words'}>
                <img
                    src={mediaUrl(params.data)}
                    className={'w-[80px] h-[80px] p-[10px] rounded-[15px] flex'}
                    alt="illustration"
                />
                <div className={'flex flex-col w-[75%] overflow-hidden'}>
                    <div className={'overflow-hidden text-ellipsis w-full'}>
                        {params.data.isGlobal && (
                            <span>
                                <img
                                    className={'max-w-[80px] max-h-[20px]'}
                                    src={logoNF}
                                    alt="logo News Factory"
                                />
                            </span>
                        )}
                        <span
                            className={
                                'relative text-medium font-medium text-dark-gray my-[10px] w-full'
                            }
                        >
                            {params.data.title}
                        </span>
                    </div>
                    <div className={'overflow-hidden text-ellipsis w-full'}>
                        <span
                            className={
                                'relative text-medium font-small text-gray w-full whitespace-normal leading-[1.5em] line-clamp-2'
                            }
                        >
                            {params.data.content}
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

function Publications() {
    const userEmail = useSelector((state: RootState) => state.user.email);
    const userGroupement = useSelector((state: RootState) => state.user.groupement);
    const userId = useSelector((state: RootState) => state.user.id);
    const isAdmin = useSelector((state: RootState) => state.user.isAdmin);
    const isSsoUser = useSelector((state: RootState) => state.user.isSsoUser);
    const isAdminGrp = !isAdmin && !isSsoUser && userGroupement?.id !== 1;
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [publications, setPublications] = useState<Publication[]>([]);
    const [searchText, setSearchText] = useState('');
    const [searchStatus, setSearchStatus] = useState<string>('');
    const [searchNetwork, setSearchNetwork] = useState<Record<string, boolean>>({
        facebook: false,
        instagram: false,
        linkedin: false,
        googleMyBusiness: false,
    });
    const [isSearching, setIsSearching] = useState<Record<string, boolean>>({});
    const [isModalOpen, setModalOpen] = useState(false);
    const [isDateOpen, setDateOpen] = useState(false);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [dateOrder, setDateOrder] = useState<Record<string, string>>({
        field: '',
        value: '',
    });
    const isFetching = useRef(false);
    const [isArchiveModalOpened, setIsArchivedModalOpened] = useState<boolean>(false);
    const [modalId, setModalId] = useState<number>(0);
    const [selectedPublications, setSelectedPublications] = useState<number[]>([]);
    const [allPublications, setAllPublications] = useState<Publication[]>([]);
    const [searchParams, setsearchParams] = useState<string>('');

    let itemsPerPage = 10;
    const url = `/api/publications`;

    const handleCloseArchiveModal = () => {
        setIsArchivedModalOpened(false);
        setModalId(0);
    };

    async function fecthAllPublications(): Promise<Publication[]> {
        setLoading(true);
        let allPublications: Publication[] = [];
        let itemsPerPage = 500;
        let fetchPage = 1;
        let hasMore = true;

        while (hasMore) {
            const response = await axios.get(
                url +
                    `?itemsPerPage=${itemsPerPage}&page=${currentPage}&order%5BcreatedAt%5D=asc` +
                    (isAdminGrp ? `&groupement=${userGroupement?.id}` : ''),
            );

            allPublications = allPublications.concat(response.data['hydra:member']);

            const allItems = response.data['hydra:totalItems'];
            hasMore = fetchPage * itemsPerPage < allItems;
            fetchPage++;
            setCurrentPage(fetchPage);
        }

        setLoading(false);

        return allPublications;
    }

    async function handleSearchText(text: string) {
        setSearchText(text);
        setCurrentPage(1);
        if (text.length >= 3) {
            setIsSearching({ text: true });
        } else {
            setIsSearching({});
        }
    }

    async function handleSearchStatus(status: string) {
        setSearchStatus(status);
        if (status === '1' || status === '2' || status === '-1') {
            setIsSearching({ status: true });
        } else {
            setIsSearching({});
        }
    }

    const handleNetworkChange = (network: string) => {
        setSearchNetwork(prev => ({
            ...prev,
            [network]: !prev[network],
        }));
        if (network) {
            setIsSearching({ network: true });
        } else {
            setIsSearching({});
        }
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleOpenDate = () => {
        setDateOpen(true);
    };

    const handleStat = async (id: number) => {
        try {
            const response = await axios.get(`/api/publication_stat/${id}`);

            if (response.data.stat) {
                const filename = `publication_stats${id}.csv`;
                const headers = [
                    'publicationId',
                    'title',
                    'network',
                    'status',
                    'message',
                    'agencyName',
                ];
                exportToCsv(filename, headers, response.data.stat);
            }
        } catch (error) {
            console.error('Error when fetching publication stat : ', error);
        }
    };

    const handleArchive = async (id: number) => {
        const date = new Date();
        try {
            const data = {
                status: 0,
                updatedBy: userEmail,
                updatedAt: format(date, 'yyyy-MM-dd'),
            };
            const response = await axios.patch(`/api/publications/${id}`, data, {
                headers: {
                    'Content-Type': 'application/merge-patch+json',
                },
            });

            if (response.status === 200) {
                setPublications(prevPublications =>
                    prevPublications.filter(publication => publication.id !== id),
                );
                setIsArchivedModalOpened(false);
            }
        } catch (error) {
            console.error(`Error when archive publication ${id} : `, error);
        }
    };

    const handleOpenArchiveModal = useCallback((id: number) => {
        setIsArchivedModalOpened(true);
        setModalId(id);
    }, []);

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleCloseDate = () => {
        setDateOpen(false);
    };

    const handleSearchDate = (value: string) => {
        setIsSearching({ date: true });
        if (value === 'CASC' || value === 'CDESC' || value === 'PASC' || value === 'PDESC') {
            switch (value) {
                case 'CASC':
                    setDateOrder({
                        field: 'order',
                        value: 'asc',
                    });
                    break;
                case 'CDESC':
                    setDateOrder({
                        field: 'order',
                        value: 'desc',
                    });
                    break;
                case 'PASC':
                    setDateOrder({
                        field: 'pubDate',
                        value: 'asc',
                    });
                    break;
                case 'PDESC':
                    setDateOrder({
                        field: 'pubDate',
                        value: 'desc',
                    });
                    break;
            }
        } else {
            setIsSearching({});
        }
    };

    const handleDateChange = (selection: any) => {
        if (selection) {
            setDateStart(format(selection.startDate, 'yyyy-MM-dd'));
            setDateEnd(format(selection.endDate, 'yyyy-MM-dd'));
            setIsSearching({ date: true });
        } else {
            setIsSearching({});
        }
    };

    const handleResetFilters = () => {
        handleSearchStatus('');
        handleNetworkChange('');
        setModalOpen(false);
    };

    const handleResetDateFilters = () => {
        handleDateChange('');
        handleSearchDate('');
        setDateOpen(false);
    };

    const headers = [
        'id',
        'title',
        'content',
        'media',
        'link',
        'social',
        'status',
        'createdAt',
        'date',
    ];

    const onExportClick = () => {
        handleExportClick(
            headers,
            fecthAllPublications,
            'publications.csv',
            () => setLoading(true),
            () => setLoading(false),
        );
    };

    useEffect(() => {
        const fetchSelectedPublications = async () => {
            try {
                const response = await axios.get(
                    `/api/user_unpublishes?user=${userId}&pagination=false`,
                );
                const selectedIds = response.data['hydra:member'].map((pub: any) => {
                    const parts = pub.publication.split('/');
                    return parseInt(parts[parts.length - 1], 10);
                });
                setSelectedPublications(selectedIds);
            } catch (error) {
                console.error(
                    'Erreur lors de la récupération des publications sélectionnées :',
                    error,
                );
            }
        };
        fetchSelectedPublications();
    }, [userId]);

    const togglePublicationSelection = async (id: number) => {
        try {
            if (selectedPublications.includes(id)) {
                const response = await axios.get(
                    `/api/user_unpublishes?user=${userId}&publication=${id}`,
                );
                const publicationToDelete = response.data['hydra:member'][0];
                if (publicationToDelete) {
                    await axios.delete(`/api/user_unpublishes/${publicationToDelete.id}`);
                    setSelectedPublications(prev => prev.filter(pubId => pubId !== id));
                }
            } else {
                await axios.post(
                    '/api/user_unpublishes',
                    {
                        user: `/api/users/${userId}`,
                        publication: `/api/publications/${id}`,
                    },
                    {
                        headers: { 'Content-Type': 'application/ld+json' },
                    },
                );
                setSelectedPublications(prev => [...prev, id]);
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la publication sélectionnée :', error);
        }
    };

    const updateSelectedPublications = useCallback(async () => {
        try {
            const response = await axios.get(
                `/api/user_unpublishes?user=${userId}&pagination=false`,
            );
            const selectedIds = response.data['hydra:member'].map((item: any) => {
                const parts = item.publication.split('/');
                return parseInt(parts[parts.length - 1], 10);
            });

            setSelectedPublications(selectedIds);
        } catch (error) {
            console.error('Erreur lors de la mise à jour des publications sélectionnées :', error);
        }
    }, [userId]);

    const fetchData = useCallback(async () => {
        if (searchText !== '' && searchText.length <= 3) {
            setAllPublications([]);
            return;
        }

        if (isFetching.current) {
            return;
        }

        isFetching.current = true;
        try {
            setLoading(true);
            let combinedPublications: Publication[] = [];
            let apiResponse: AxiosResponse<any, any>;
            const filters = Object.entries(searchNetwork)
                .filter(([_, value]) => value)
                .map(([key]) => key);
            const socialParams = filters.map(network => `social=${network}`).join('&');

            for (const [key] of Object.entries(isSearching)) {
                switch (key) {
                    case 'text':
                        setsearchParams(`?title=${searchText}`);
                        break;
                    case 'status':
                        setsearchParams(`?status=${searchStatus}`);
                        break;
                    case 'network':
                        if (searchStatus) {
                            setsearchParams(`?status=${searchStatus}&${socialParams}`);
                        } else {
                            setsearchParams(`?${socialParams}`);
                        }
                        break;
                    case 'date':
                        if (dateOrder.field && dateOrder.value && dateStart && dateEnd) {
                            setsearchParams(
                                `?fromDate=${dateStart}&toDate=${dateEnd}&order=${dateOrder.value}`,
                            );
                        } else if (dateOrder.field && dateOrder.value) {
                            setsearchParams(`?${dateOrder.field}=${dateOrder.value}`);
                        } else {
                            setsearchParams(`?fromDate=${dateStart}&toDate=${dateEnd}`);
                        }
                }
            }

            if (isSsoUser) {
                const userUrl = `/api/publications-all-accessible-for-user/` + userId;
                const publicationUrl = searchParams
                    ? userUrl + searchParams
                    : userUrl + '?referer=page-publication';
                const userResponse = await axios.get(publicationUrl);
                combinedPublications = combinedPublications.concat(userResponse.data);
            } else {
                const adminUrl = `/api/publications-all-accessible-for-admin`;
                const publicationUrl = searchParams
                    ? adminUrl + searchParams
                    : adminUrl + '?referer=page-publication';
                apiResponse = await axios.get(publicationUrl);
                combinedPublications = apiResponse.data;
            }
            const uniquePublications = Array.from(
                new Map(combinedPublications.map(pub => [pub.id, pub])).values(),
            );
            setTotalPage(Math.ceil(combinedPublications.length / itemsPerPage));
            setAllPublications(uniquePublications);
        } catch (error) {
            console.error('Erreur lors de la récupération des publications :', error);
        } finally {
            isFetching.current = false;
            setLoading(false);
        }
    }, [
        searchParams,
        dateEnd,
        dateOrder.field,
        dateOrder.value,
        dateStart,
        isSearching,
        isSsoUser,
        itemsPerPage,
        searchNetwork,
        searchStatus,
        searchText,
        userId,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData, currentPage, searchText, searchStatus]);

    useEffect(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setPublications(allPublications.slice(startIndex, endIndex));
    }, [allPublications, currentPage, itemsPerPage]);

    function dateGenerator(dateAsString: any) {
        let newDate = new Date(dateAsString);
        let day_month = newDate.toLocaleString('fr-FR', {
            day: 'numeric',
            month: 'short',
        });
        let year = newDate.toLocaleString('fr-FR', { year: 'numeric' });
        return (
            <>
                <div className="flex items-center h-full w-full flex-col justify-center leading-normal">
                    <span className="text-base font-semibold">{day_month}</span>
                    <span className="text-sm font-normal">{year}</span>
                </div>
            </>
        );
    }

    function dateFormatter(params: any) {
        return dateGenerator(params.data.publicationDate);
    }

    function createdAtFormatter(params: any) {
        return dateGenerator(params.data.createdAt);
    }

    function actionButtons(params: any) {
        let id = params.data.id;
        let status = params.data.status;
        let isGlobal = params.data.isGlobal;
        let createdByAdminUrl = params.data.createdByAdmin;
        let createdByUserUrl = params.data.createdByUser;
        const extractIdFromUrl = (url: string) => {
            if (!url) return null;
            const parts = url.split('/');
            return parts.length > 0 ? parseInt(parts[parts.length - 1]) : null;
        };
        const createdByAdminId = extractIdFromUrl(createdByAdminUrl);
        const createdByUserId = extractIdFromUrl(createdByUserUrl);

        const isCreatedByAdmin = createdByAdminId === userId;
        const isCreatedByUser = createdByUserId === userId;
        const isSelected = !selectedPublications.includes(id);

        return (
            <>
                {isGlobal ? (
                    <div className="flex items-center justify-center h-full w-full gap-3">
                        {status === 2 ? (
                            <>
                                {isCreatedByAdmin ? (
                                    <>
                                        <Link to={`/show_publication/${id}`}>
                                            <img
                                                src={visibility}
                                                className={'w-[20px] h-[20px]'}
                                                alt="visibility icon"
                                            />
                                        </Link>
                                        <button onClick={() => handleStat(id)} className={'p-0'}>
                                            <img
                                                src={stat}
                                                className={'w-[20px] h-[20px]'}
                                                alt="stat icon"
                                            />
                                        </button>
                                        <button
                                            onClick={() => handleOpenArchiveModal(id)}
                                            className={'p-0'}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <Link to={`/show_publication/${id}`}>
                                        <img
                                            src={visibility}
                                            className={'w-[20px] h-[20px]'}
                                            alt="visibility icon"
                                        />
                                    </Link>
                                )}
                            </>
                        ) : (
                            <>
                                {isCreatedByAdmin ? (
                                    <>
                                        <Link to={`/edit_publication/${id}`}>
                                            <img
                                                src={editOn}
                                                className={'w-[20px] h-[20px]'}
                                                alt="edit icon"
                                            />
                                        </Link>
                                        <button
                                            onClick={() => handleOpenArchiveModal(id)}
                                            className={'p-0'}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <div className="relative inline-flex items-center gap-2">
                                        <Link to={`/show_publication/${id}`}>
                                            <img
                                                src={visibility}
                                                className={'w-[20px] h-[20px]'}
                                                alt="visibility icon"
                                            />
                                        </Link>
                                        {isSsoUser && (
                                            <button
                                                type={'button'}
                                                onClick={() => togglePublicationSelection(id)}
                                                className={
                                                    'p-0 relative bg-transparent border-0 cursor-pointer'
                                                }
                                                title={
                                                    isSelected
                                                        ? 'Déprogrammer la publication'
                                                        : 'Programmer la publication'
                                                }
                                            >
                                                <img
                                                    src={isSelected ? progPubli : unselected}
                                                    className={'w-[20px] h-[20px]'}
                                                    alt="toggle publi icon"
                                                />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full w-full gap-3">
                        {status === 2 ? (
                            <>
                                {isCreatedByAdmin || isCreatedByUser ? (
                                    <>
                                        <button
                                            className={'p-0'}
                                            onClick={() => handleOpenArchiveModal(id)}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <img
                                        src={visibility}
                                        className={'w-[20px] h-[20px]'}
                                        alt="visibility icon"
                                        style={{ opacity: 0.5 }}
                                    />
                                )}
                                {(isCreatedByAdmin || isCreatedByUser) && (
                                    <Link to={`/show_publication/${id}`}>
                                        <img
                                            src={visibility}
                                            className={'w-[20px] h-[20px]'}
                                            alt="visibility icon"
                                        />
                                    </Link>
                                )}
                            </>
                        ) : (
                            <>
                                {isCreatedByAdmin || isCreatedByUser ? (
                                    <>
                                        <Link to={`/edit_publication/${id}`}>
                                            <img
                                                src={editOn}
                                                className={'w-[20px] h-[20px]'}
                                                alt="edit icon"
                                            />
                                        </Link>
                                        <button
                                            className={'p-0'}
                                            onClick={() => handleOpenArchiveModal(id)}
                                        >
                                            <img
                                                src={archive}
                                                className={'w-[20px] h-[20px]'}
                                                alt="archive icon"
                                            />
                                        </button>
                                    </>
                                ) : (
                                    <Link to={`/show_publication/${id}`}>
                                        <img
                                            src={visibility}
                                            className={'w-[20px] h-[20px]'}
                                            alt="visibility icon"
                                        />
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                )}
            </>
        );
    }

    const defaultColDef = useMemo(() => {
        return {
            resizable: false,
            sortable: false,
            suppressMovable: true,
        };
    }, []);

    const columnDefs: ColDef<Publication>[] = [
        {
            headerName: 'Publication',
            cellRenderer: publicationCard,
            autoHeight: true,
            flex: 3,
        },
        { headerName: 'Status', cellRenderer: statusCellRenderer, flex: 1 },
        {
            headerName: 'Créée Le',
            field: 'createdAt',
            sortable: true,
            cellRenderer: createdAtFormatter,
            flex: 1,
        },
        {
            headerName: 'Publiée Le',
            field: 'publicationDate',
            sortable: true,
            cellRenderer: dateFormatter,
            flex: 1,
        },
        {
            headerName: 'Réseaux Sociaux',
            cellRenderer: socialMediaCellRenderer,
            flex: 1,
        },
        { headerName: 'Action', cellRenderer: actionButtons, flex: 1 },
    ];

    const containerStyle = useMemo(
        () => ({
            width: '100%',
            height: '-webkit-fill-available',
            fontFamily: 'Montserrat',
        }),
        [],
    );

    return (
        <>
            <div className="flex flex-1 relative w-full h-screen">
                <div className="flex-none relative min-w-64">
                    <Navbar />
                </div>
                <div className="flex-1 relative">
                    <div className="flex flex-col items-start relative h-full">
                        <div className="flex flex-col py-10 px-9 w-full">
                            <div className="flex items-center justify-between w-full pb-8 border-b border-light-gray">
                                <div className="flex flex-col items-start gap-1 relative">
                                    <h1 className="text-dark-gray text-super-heavy font-heavy m-0 p-0">
                                        Mes publications
                                    </h1>
                                    <p className="text-gray m-0 p-0">
                                        Retrouvez l'ensemble de vos publications sur cette page
                                    </p>
                                </div>
                                <div className="flex items-center justify-center">
                                    <Link
                                        className="flex leading-6 rounded-[1.5rem] w-fit text-medium font-medium border border-light-gray bg-white items-center content-center px-[10px] py-[10px] text-dark-gray flex-row justify-center"
                                        to={'/archived_publications'}
                                    >
                                        Mes publications archivées
                                    </Link>
                                    <AddPublicationButton />
                                </div>
                            </div>
                            {(isSsoUser || isAdminGrp) &&
                                (() => {
                                    const hasSpecificGroupement =
                                        userGroupement && userGroupement.id !== 1;
                                    const showLogoSwitch = !hasSpecificGroupement || isAdminGrp;
                                    const showBothSwitches = isSsoUser && showLogoSwitch;

                                    return (
                                        <div
                                            style={{
                                                alignSelf: 'stretch',
                                                justifyContent: 'flex-start',
                                                alignItems: 'flex-start',
                                                gap: 24,
                                                display: 'flex',
                                                marginBottom: 24,
                                                width: '100%',
                                            }}
                                        >
                                            {isSsoUser && (
                                                <AutoPubSwitch
                                                    updateSelectedPublications={
                                                        updateSelectedPublications
                                                    }
                                                    newStyle={showBothSwitches}
                                                />
                                            )}
                                            <LogoSwitch newStyle={showBothSwitches} />
                                        </div>
                                    );
                                })()}

                            <div className={'relative flex w-full justify-between pb-8'}>
                                <div className="flex relative">
                                    <input
                                        type="text"
                                        className="flex w-full min-w-80 max-w-80 text-small"
                                        placeholder="Rechercher une publication..."
                                        value={searchText}
                                        onChange={e => handleSearchText(e.target.value)}
                                    />
                                    <img
                                        src={search.path}
                                        alt="search icon"
                                        className="-ml-7 mt-4 w-4 h-4"
                                    />
                                </div>
                                <div className={'flex'}>
                                    {!isSsoUser && (
                                        <button
                                            className="flex items-center text-dark-gray text-medium font-medium bg-primary-light"
                                            onClick={onExportClick}
                                            disabled={loading}
                                        >
                                            <img
                                                src={download.path}
                                                alt="download icon"
                                                className="mr-2 w-8 h-8"
                                            />
                                            Exporter
                                        </button>
                                    )}
                                    <button
                                        className="flex items-center text-dark-gray text-medium font-medium bg-primary-light"
                                        onClick={handleOpenModal}
                                    >
                                        <img
                                            src={sort.path}
                                            alt="sort icon"
                                            className="ml-4 mr-2 w-6 h-6"
                                        />
                                        Trier par
                                    </button>
                                    <button
                                        className="flex items-center text-dark-gray text-medium font-medium bg-primary-light"
                                        onClick={handleOpenDate}
                                    >
                                        <img
                                            src={date.path}
                                            alt="date icon"
                                            className="ml-4 mr-3 w-5 h-5"
                                        />
                                        Date
                                    </button>
                                </div>
                                <Filter isOpen={isModalOpen} onClose={handleCloseModal}>
                                    <div className="flex flex-row justify-between w-full border-b border-light-gray pb-[12px]">
                                        <span className="text-super-medium font-medium text-dark-gray">
                                            Trier par
                                        </span>
                                        <Link
                                            className="text-medium font-medium text-primary mt-1"
                                            onClick={() => handleResetFilters()}
                                            to=""
                                        >
                                            Réinitialiser
                                        </Link>
                                    </div>
                                    <div className="flex w-full flex-col border-b border-light-gray pb-3">
                                        <span className="flex w-full text-medium font-medium pb-3">
                                            Status
                                        </span>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'1'}
                                                name={'status'}
                                                onChange={e => handleSearchStatus(e.target.value)}
                                            />
                                            <label
                                                htmlFor="en_cours"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoInProgressColored}
                                                    alt="en cours"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Status en cours
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'2'}
                                                name={'status'}
                                                onChange={e => handleSearchStatus(e.target.value)}
                                            />
                                            <label
                                                htmlFor="terminee"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoFinishedColored}
                                                    alt="terminé"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Status terminée
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'-1'}
                                                name={'status'}
                                                onChange={e => handleSearchStatus(e.target.value)}
                                            />
                                            <label
                                                htmlFor="brouillon"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoDraftColored}
                                                    alt="brouillon"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Brouillon
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex w-full flex-col">
                                        <span className="flex w-full text-medium font-medium pb-3">
                                            Réseaux sociaux
                                        </span>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'facebook'}
                                                value={'facebook'}
                                                checked={searchNetwork.facebook}
                                                onChange={() => handleNetworkChange('facebook')}
                                            />
                                            <label
                                                htmlFor="facebook"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoFbColored}
                                                    alt="Facebook"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Facebook
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'instagram'}
                                                value={'instagram'}
                                                checked={searchNetwork.instagram}
                                                onChange={() => handleNetworkChange('instagram')}
                                            />
                                            <label
                                                htmlFor="instagram"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoInstaColored}
                                                    alt="Instagram"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Instagram
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'linkedin'}
                                                value={'linkedin'}
                                                checked={searchNetwork.linkedin}
                                                onChange={() => handleNetworkChange('linkedin')}
                                            />
                                            <label
                                                htmlFor="linkedin"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoInColored}
                                                    alt="linkedin"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Linkedin
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="checkbox"
                                                id={'googleMyBusiness'}
                                                value={'googleMyBusiness'}
                                                checked={searchNetwork.googleMyBusiness}
                                                onChange={() =>
                                                    handleNetworkChange('googleMyBusiness')
                                                }
                                            />
                                            <label
                                                htmlFor="gmb"
                                                className="flex text-gray text-medium m-0 items-center"
                                            >
                                                <img
                                                    src={logoGmbColored}
                                                    alt="google my business"
                                                    className={'flex h-[26px] w-[26px] px-[5px]'}
                                                />
                                                Google My Business
                                            </label>
                                        </div>
                                    </div>
                                </Filter>
                                <Filter isOpen={isDateOpen} onClose={handleCloseDate}>
                                    <Link
                                        className="text-primary ml-[70%] mt-[10px]"
                                        onClick={() => handleResetDateFilters()}
                                        to=""
                                    >
                                        Réinitialiser
                                    </Link>
                                    <span>Date</span>
                                    <div>
                                        <p>Création / Publication</p>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'CASC'}
                                                name={'dateOrder'}
                                                onChange={e => handleSearchDate(e.target.value)}
                                            />
                                            <label
                                                className="flex m-0 text-medium text-gray"
                                                htmlFor="Date de création croissante"
                                            >
                                                Date de création croissante
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'CDESC'}
                                                name={'dateOrder'}
                                                onChange={e => handleSearchDate(e.target.value)}
                                            />
                                            <label
                                                className="flex m-0 text-medium text-gray"
                                                htmlFor="terminee"
                                            >
                                                Date de création décroissante
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'PASC'}
                                                name={'dateOrder'}
                                                onChange={e => handleSearchDate(e.target.value)}
                                            />
                                            <label
                                                className="flex m-0 text-medium text-gray"
                                                htmlFor="Date de création croissante"
                                            >
                                                Date de publication croissante
                                            </label>
                                        </div>
                                        <div className="flex w-full items-center content-center">
                                            <input
                                                type="radio"
                                                value={'PDESC'}
                                                name={'dateOrder'}
                                                onChange={e => handleSearchDate(e.target.value)}
                                            />
                                            <label
                                                className="flex m-0 text-medium text-gray"
                                                htmlFor="terminee"
                                            >
                                                Date de publication décroissante
                                            </label>
                                        </div>
                                    </div>
                                    <div className={'relative w-fit'}>
                                        <p>Par période</p>
                                        <div className={'z-[1000]'}>
                                            <Link
                                                className="text-primary ml-[68%] mt-[10px]"
                                                onClick={() => handleResetDateFilters()}
                                                to=""
                                            >
                                                Réinitialiser
                                            </Link>
                                            <DateRangeComponent onDateChange={handleDateChange} />
                                        </div>
                                    </div>
                                </Filter>
                            </div>
                            <div style={containerStyle}>
                                <div className="ag-theme-alpine" style={containerStyle}>
                                    <Spinner loading={loading} />
                                    <AgGridReact
                                        rowData={publications}
                                        defaultColDef={defaultColDef}
                                        columnDefs={columnDefs}
                                        domLayout="autoHeight"
                                        pagination={false}
                                        overlayNoRowsTemplate={'Pas de données enregistrées'}
                                        suppressNoRowsOverlay={!!publications}
                                    />
                                </div>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPage={totalPage}
                                    previous={() => setCurrentPage(p => p - 1)}
                                    next={() => setCurrentPage(p => p + 1)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={isArchiveModalOpened}
                onClose={handleCloseArchiveModal}
                className={'flex flex-col justify-between w-full min-h-48'}
            >
                <div
                    className={
                        'flex flex-col justify-between items-center w-full h-full flex-1 text-center'
                    }
                >
                    <span className="text-heavy font-super-heavy">
                        Etes-vous sur de vouloir archiver <br />
                        cette publication ?
                    </span>
                    <span className="my-8">Cette publication ne sera pas publiée.</span>
                    <div className={'flex justify-between items-center w-full h-full'}>
                        <button
                            className={
                                'px-4 py-2 leading-[1.5] rounded-[1.5rem] flex justify-center items-center w-[200px] text-small font-medium mb-2.5 bg-primary border border-light-gray'
                            }
                            onClick={() => handleArchive(modalId)}
                        >
                            Oui
                        </button>
                        <button
                            className={
                                'px-4 py-2 leading-[1.5] rounded-[1.5rem] flex justify-center items-center w-[200px] text-small font-medium mb-2.5 bg-white border border-light-gray'
                            }
                            onClick={handleCloseArchiveModal}
                        >
                            Non
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default Publications;
